import { yupResolver } from '@hookform/resolvers/yup';
import type { ImageProps } from 'next/image';
import React from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Button } from '@/components/Button';
import { Col } from '@/components/Col';
import { Container } from '@/components/Container';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { Row } from '@/components/Row';
import { ServerErrors } from '@/components/ServerErrors';
import { Typography } from '@/components/Typography';
import type { ContentfulPlaceholderServiceFragment } from '@/graphql/contentful/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { RecommendPrice } from '@/lib/service/type';
import { generateDataTestId } from '@/lib/tests';
import type { ApiErrorWithType, WithHTMLProp } from '@/lib/types';
import { serviceMessages } from '@/messages/service';

import { FormSearch } from '../FormSearch';
import { PriceTable } from '../PriceTable';
import type { IdentifyWatchSchema } from './schema';
import { identifyWatchSchema } from './schema';

import * as S from './IdentifyWatch.styles';

export type ContinueParams = IdentifyWatchSchema;

export type IdentifyWatchProps = ContentfulPlaceholderServiceFragment &
  WithHTMLProp<'className'> & {
    errors?: ApiErrorWithType[];
    hiddenTitle?: boolean;
    icon?: ImageProps['src'];
    isProcessing?: boolean;
    onSkip?: () => void;
    onSubmit?: (params: ContinueParams) => void;
    submitText: string;
    variant?: 'default' | 'lightGrey';
  };

export const IdentifyWatch = ({
  errors,
  isProcessing,
  onSkip,
  onSubmit,
  submitText,
  variant = 'default',
  ...props
}: IdentifyWatchProps) => {
  const { formatMessage } = useIntl();
  const schema = identifyWatchSchema(formatMessage);
  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });
  const { sendEvent } = useGoogleAnalytics();

  const handleSkip = () => {
    sendEvent({
      event: EVENT_NAME.SERVICE_REQUEST,
      step: 1,
    });
    onSkip && onSkip();
  };

  const handleSubmit: SubmitHandler<IdentifyWatchSchema> = (props) => {
    sendEvent({
      event: EVENT_NAME.SERVICE_REQUEST,
      step: 1,
    });
    onSubmit && onSubmit(props);
  };

  return (
    <FormSearch
      bg={variant === 'lightGrey' ? 'lightGrey' : 'transparent'}
      data-test-id='IdentifyWatchFormSearch'
      {...props}
    >
      <FormProvider
        data-test-id='IdentifyWatchFormProvider'
        name='identifyWatch'
        {...formCtx}
        onSubmit={handleSubmit}
      >
        <S.FormWrapper data-test-id='IdentifyWatchFormWrapper'>
          {errors && (
            <ServerErrors
              data-test-id='IdentifyWatchServerErrors'
              errors={errors}
            />
          )}
          <InputField
            aria-label={formatMessage(serviceMessages.serialNumber)}
            colorVariant={variant === 'lightGrey' ? 'white' : 'grey'}
            data-test-id='IdentifyWatchInputFieldSN'
            disabled={isProcessing}
            name='sn'
            placeholder={formatMessage(serviceMessages.serialNumber)}
            required
          />
          <Row>
            <Col xs={6}>
              <Button
                data-test-id='IdentifyWatchButtonSkip'
                isFullWidth
                onClick={handleSkip}
                variant='outlined'
                {...generateDataTestId('button', 'skip')}
              >
                {formatMessage(serviceMessages.skip)}
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                data-test-id='IdentifyWatchButtonSubmit'
                disabled={isProcessing}
                isFullWidth
                isProcessing={isProcessing}
                type='submit'
              >
                {submitText}
              </Button>
            </Col>
          </Row>
        </S.FormWrapper>
      </FormProvider>
    </FormSearch>
  );
};

interface ResultProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  prices: RecommendPrice[];
  sn: string;
}

const Result = ({ onClick, prices, sn }: ResultProps) => {
  const { formatMessage } = useIntl();
  return (
    <Container data-test-id='IdentifyWatchResultContainer' maxWidth='md'>
      <Typography
        data-test-id='IdentifyWatchResultTypography'
        mb={2}
        scale='h4'
        textAlign='center'
        textTransform='uppercase'
      >
        {formatMessage(serviceMessages.resultsForSerialNumber)}&nbsp;&quot;
        <Typography
          as='span'
          color='primary'
          data-test-id='IdentifyWatchResultTypographySN'
          scale='h4'
        >
          {sn}
        </Typography>
        &quot;
      </Typography>
      <PriceTable
        data-test-id='IdentifyWatchResultPriceTable'
        prices={prices}
      ></PriceTable>
      <S.ButtonWrapper
        data-test-id='IdentifyWatchResultButtonWrapper'
        maxWidth={500}
        mt={2}
      >
        <Row>
          <Col sm={6} smStart={4} xs={12} xsStart={1}>
            <Button
              data-test-id='IdentifyWatchResultButtonRequest'
              isFullWidth
              onClick={onClick}
              {...generateDataTestId('button', 'request')}
            >
              {formatMessage(serviceMessages.requestAService)}
            </Button>
          </Col>
        </Row>
      </S.ButtonWrapper>
    </Container>
  );
};

IdentifyWatch.Result = Result;

IdentifyWatch.displayName = 'IdentifyWatch';
